import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';
import { captureAttribution } from '../lib/attribution';
import { onSeoCommit } from '../lib/seoCommit';

/*
 * Pushes `spa_page_view` to window.dataLayer once per route view — including the very first
 * page load (tracking brief Rev C: the Google tag inside GTM runs with send_page_view off, so
 * without the first-load push the landing page of every session would go unrecorded).
 *
 * Why not GA4's built-in page view / history change: the SEO layer (react-helmet-async) sets
 * document.title a beat after the URL changes, so a history-based hit would carry the previous
 * page's title (or the generic template title on first load). Every page renders <Seo>, whose
 * Helmet reports each committed head update through lib/seoCommit.js. We wait for the commit
 * that belongs to THIS route (path + query), then push once with the correct path, title, and
 * location. A 2.5s fallback covers a route that never commits a title (e.g. /admin).
 *
 * Exactly-once guards:
 *   - module-level `lastHandledKey` survives StrictMode's double effect run and re-renders
 *     that do not change the path
 *   - `navSeq` drops a pending push when a newer navigation supersedes it, so a redirect
 *     (e.g. /reviews → /projects) records one view, not two
 * No /admin special-casing here: the container excludes /admin at the trigger level.
 * This module deliberately imports nothing from the SEO manifest so the shared bundle stays lean.
 */
const TITLE_WAIT_MS = 2500;
let lastHandledKey = null;
let navSeq = 0;

export const PageViewTracker = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const key = `${pathname}${search}`;
    // brief 02: remember first-touch utm_* / landing path for the calculator's estimate records.
    captureAttribution();
    if (key === lastHandledKey) return undefined; // StrictMode re-run or a re-render without navigation
    lastHandledKey = key;

    const seq = ++navSeq;
    let fired = false;
    let unsubscribe = () => {};
    let fallback = null;
    const fire = () => {
      if (fired || seq !== navSeq) return; // already pushed, or superseded by a newer navigation
      fired = true;
      unsubscribe();
      if (fallback) clearTimeout(fallback);
      trackPageView({ pagePath: key, pageTitle: document.title, pageLocation: window.location.href });
    };
    // Fires synchronously if this route's <Seo> already committed (first-load race), otherwise
    // on the commit event; the timer is the safety net for routes without <Seo>.
    unsubscribe = onSeoCommit(key, fire);
    if (!fired) fallback = setTimeout(fire, TITLE_WAIT_MS);
    return undefined; // intentionally no cleanup: a StrictMode re-run must not cancel the pending push
  }, [pathname, search]);

  return null;
};
